import type { ICartGroup, ICartItem } from '@/api/types/cart';
import { sha256 } from 'js-sha256';
import logger from '@/utils/client-logger';
import { collectEligibleCartIds } from './groups';
import {
  createCartSelectionSnapshot,
  getCartSelectionStorageKey,
  parseCartSelectionSnapshot,
} from './snapshot';
import {
  CART_SELECTION_TTL_MS,
  type CartSelectionScope,
  type RestoredCartSelection,
} from './types';

export const CART_GUEST_MERGE_KEY = 'zl-cart-v1-guest-merge:v1';
const RECENT_ADD_KEY = 'zl-cart-v1-recent-add:v1';
type GuestRow = { cartId: number; configuration: string | null; selected: boolean };
type GuestMerge = {
  version: 1;
  scope: CartSelectionScope;
  createdAt: number;
  expiresAt: number;
  selectionExpiresAt: number | null;
  rows: GuestRow[];
};
const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const validId = (id: unknown): id is number => Number.isSafeInteger(id) && Number(id) > 0;

/** 忽略数量、价格及展示文案；完整配置散列，不将处方内容写入迁移凭据。 */
const itemConfiguration = (item: ICartItem) => {
  const productId = item.product_id ?? item.product?.id;
  if (!validId(productId)) return null;
  const option = Object.entries(item.option ?? {})
    .filter(
      ([key, value]) =>
        ![
          'prescription_type_name',
          'lens_id',
          'coating_id',
          'is_pending_lens',
          'old_lens_price',
          'old_lens_status',
          'old_lens_id',
        ].includes(key) &&
        value !== null &&
        value !== undefined &&
        value !== '',
    )
    .map(([key, value]) => [
      key,
      typeof value === 'string' && /^[+-]?\d+(\.\d+)?$/.test(value) ? Number(value) : value,
    ])
    .sort(([a], [b]) => String(a).localeCompare(String(b)));
  return [
    productId,
    Number(item.option?.lens_id ?? item.lens?.lens_id ?? 0),
    Number(item.option?.coating_id ?? item.coating?.coating_id ?? 0),
    item.option?.is_pending_lens ?? 0,
    item.prescriptions_image ?? '',
    option,
  ];
};
const groupConfiguration = (group: ICartGroup): string | null => {
  const items = group.carts?.map(itemConfiguration);
  if (!items?.length || items.some((item) => item === null)) return null;
  return sha256(JSON.stringify([group.type ?? 1, group.relation_id ?? 0, items]));
};

/** 仅附加 ID/配置散列，保存在既有勾选快照里，刷新后仍能识别后端合并条目。 */
export const describeCartSelectionGroups = (groups: readonly ICartGroup[]) =>
  groups.flatMap((group) => {
    const cartId = group.carts?.[0]?.cart_id;
    return validId(cartId) ? [{ cartId, configuration: groupConfiguration(group) }] : [];
  });
const validRow = (row: unknown): row is Pick<GuestRow, 'cartId' | 'configuration'> =>
  record(row) &&
  validId(row.cartId) &&
  (row.configuration === null ||
    (typeof row.configuration === 'string' && /^[a-f0-9]{64}$/.test(row.configuration)));

/** 必须在 token 切换的同步通知中捕获：此时 Store 仍持有游客列表。 */
export function captureCartGuestMerge({
  guestScope,
  sessionIdentity,
  groups,
  storage,
  pendingStorage,
  now = Date.now(),
}: {
  guestScope: CartSelectionScope;
  sessionIdentity: string;
  groups: readonly ICartGroup[];
  storage: Storage;
  pendingStorage: Storage;
  now?: number;
}) {
  const raw = storage.getItem(getCartSelectionStorageKey(guestScope));
  const snapshot = parseCartSelectionSnapshot(raw, { scope: guestScope, now });
  const parsed: unknown = snapshot && raw ? JSON.parse(raw) : null;
  const selectionExpiresAt =
    record(parsed) && typeof parsed.expiresAt === 'number' ? parsed.expiresAt : null;
  const selected = new Set(snapshot?.selectedCartIds ?? []);
  // 游客可能在商品页加购后直接登录，成功加购凭据尚未由购物车页消费。
  // 仅在已有有效勾选记录时续接自动勾选；没有记录仍遵守合并后的条目数默认规则。
  const recentRaw = storage.getItem(RECENT_ADD_KEY);
  let recent: unknown = null;
  try {
    recent = recentRaw ? JSON.parse(recentRaw) : null;
  } catch {
    /* 损坏的加购凭据不影响合并。 */
  }
  if (
    snapshot &&
    record(parsed) &&
    record(recent) &&
    recent.version === 1 &&
    recent.identity === guestScope.identity &&
    recent.site === guestScope.site &&
    recent.currency === guestScope.currency &&
    typeof recent.createdAt === 'number' &&
    Number.isFinite(recent.createdAt) &&
    recent.createdAt <= now &&
    now - recent.createdAt < 5 * 60_000 &&
    Array.isArray(recent.cartIds) &&
    recent.cartIds.length > 0 &&
    recent.cartIds.every(validId)
  ) {
    const hasConfigurations =
      Array.isArray(parsed.configurations) && parsed.configurations.every(validRow);
    const knownIds = new Set(snapshot.selectedCartIds);
    if (hasConfigurations) {
      (parsed.configurations as Pick<GuestRow, 'cartId' | 'configuration'>[]).forEach((row) =>
        knownIds.add(row.cartId),
      );
    }
    recent.cartIds.forEach((id: number) => {
      // 分批响应保存了较新快照，但未返回目标的凭据保持原时间；未出现条目仍须自动选。
      // 已在快照中的条目则不能用旧凭据覆盖用户后来主动取消的选择。
      if (
        (recent.createdAt as number) >= Number(parsed.updatedAt) ||
        (hasConfigurations && !knownIds.has(id))
      )
        selected.add(id);
    });
  }
  const descriptions = new Map<number, string | null>();
  if (snapshot) {
    // 兼容旧快照没有配置的情形：稳定 cart_id 仍可直接跟随游客。
    selected.forEach((id) => descriptions.set(id, null));
    if (record(parsed) && Array.isArray(parsed.configurations)) {
      parsed.configurations
        .filter(validRow)
        .forEach((row) => descriptions.set(row.cartId, row.configuration));
    }
  }
  describeCartSelectionGroups(groups).forEach((row) =>
    descriptions.set(row.cartId, row.configuration),
  );
  const rows: GuestRow[] = Array.from(descriptions, ([cartId, configuration]) => ({
    cartId,
    configuration,
    selected: selected.has(cartId),
  }));
  const signal: GuestMerge = {
    version: 1,
    scope: { ...guestScope, identity: sessionIdentity },
    createdAt: now,
    expiresAt: now + CART_SELECTION_TTL_MS,
    selectionExpiresAt,
    rows,
  };
  pendingStorage.setItem(CART_GUEST_MERGE_KEY, JSON.stringify(signal));
  // 先捕获再清理：新账号加购 producer 不能将前一会话的历史 ID 续入新凭据。
  storage.removeItem(RECENT_ADD_KEY);
}

/** 只在登录后首次初始化使用；没有游客有效记录时明确覆盖账号历史快照。 */
export function consumeCartGuestMerge({
  scope,
  sessionIdentity,
  groups,
  eligibleGroups,
  storage,
  pendingStorage,
  now = Date.now(),
}: {
  scope: CartSelectionScope;
  sessionIdentity: string | null;
  groups: readonly ICartGroup[];
  eligibleGroups: readonly ICartGroup[];
  storage: Storage;
  pendingStorage: Storage;
  now?: number;
}): { snapshot: RestoredCartSelection; createdAt: number } | null {
  const raw = pendingStorage.getItem(CART_GUEST_MERGE_KEY);
  if (!raw || !scope.identity || !sessionIdentity) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    pendingStorage.removeItem(CART_GUEST_MERGE_KEY);
    return null;
  }
  if (
    !record(value) ||
    value.version !== 1 ||
    !record(value.scope) ||
    typeof value.createdAt !== 'number' ||
    !Number.isFinite(value.createdAt) ||
    value.createdAt > now ||
    value.expiresAt !== value.createdAt + CART_SELECTION_TTL_MS ||
    now >= Number(value.expiresAt) ||
    (value.selectionExpiresAt !== null &&
      (typeof value.selectionExpiresAt !== 'number' ||
        !Number.isFinite(value.selectionExpiresAt) ||
        value.selectionExpiresAt < 0 ||
        value.selectionExpiresAt > Number(value.expiresAt))) ||
    !Array.isArray(value.rows) ||
    !value.rows.every(
      (row: unknown) =>
        record(row) &&
        validId(row.cartId) &&
        (row.configuration === null ||
          (typeof row.configuration === 'string' && /^[a-f0-9]{64}$/.test(row.configuration))) &&
        typeof row.selected === 'boolean',
    )
  ) {
    pendingStorage.removeItem(CART_GUEST_MERGE_KEY);
    return null;
  }
  if (
    value.scope.site !== scope.site ||
    value.scope.currency !== scope.currency ||
    value.scope.identity !== sessionIdentity
  )
    return null;
  const rows = value.rows as GuestRow[];
  const eligible = collectEligibleCartIds(eligibleGroups);
  const hasRecord = typeof value.selectionExpiresAt === 'number' && now < value.selectionExpiresAt;
  const selectedCartIds = hasRecord
    ? eligible.filter((cartId) => {
        const group = groups.find((item) => item.carts?.[0]?.cart_id === cartId);
        if (!group) return false;
        const configuration = groupConfiguration(group);
        // cart_id 是后端稳定条目标识；改价/展示元数据不改变该条目的身份。
        const direct = rows.find((row) => row.cartId === cartId);
        if (direct) return direct.selected;
        // 后端合并可能更换 cart_id；仅接受唯一的完整配置匹配，不按商品 ID 猜测。
        if (!configuration) return false;
        const source = rows.filter((row) => row.configuration === configuration);
        const targets = groups.filter((item) => groupConfiguration(item) === configuration);
        return source.length === 1 && targets.length === 1 && source[0].selected;
      })
    : eligible.length === 1
      ? eligible
      : [];
  const snapshot = { selectedCartIds, explicitlyEmpty: selectedCartIds.length === 0 };
  // 先落盘再消费，确保 token 临时身份→用户身份的后续初始化也能恢复同一选择。
  const persisted = createCartSelectionSnapshot({ scope, now, ...snapshot });
  if (hasRecord) {
    persisted.expiresAt = value.selectionExpiresAt as number;
    persisted.updatedAt = persisted.expiresAt - CART_SELECTION_TTL_MS;
  }
  try {
    storage.setItem(getCartSelectionStorageKey(scope), JSON.stringify(persisted));
    if (sessionIdentity !== scope.identity) {
      // 已有账号会话旧快照不能在下一次资料恢复/刷新时覆盖游客合并选择。
      storage.removeItem(getCartSelectionStorageKey({ ...scope, identity: sessionIdentity }));
    }
    pendingStorage.removeItem(CART_GUEST_MERGE_KEY);
  } catch (error) {
    // 存储失败仍应用本次正确选择，保留凭据供后续身份恢复重试，不回显账号旧选择。
    logger.error('Failed to persist merged guest cart selection:', error);
  }
  return { snapshot, createdAt: value.createdAt };
}
